import * as React from 'react'
import { CircleProps, GoogleMap } from 'react-google-maps'
import {
  IActiveTripResponseV2,
  ICoordinates,
  IGetGoogleMapPolyline,
  IMarker,
  IOSMDirectionReq,
  IRiderLocationObject,
  IServiceLocationObject,
  IWaypoints,
  IZoneObject
} from '../types'
import { ICoordinatesWithShortKeyDTO } from '../types/google-map'
import GoogleMapComponent from './google-map/GoogleMap'

interface IStrategy {
  renderConditions: IRenderConditions
  toFind: (stage: string) => boolean
}

export interface IRenderConditions {
  isShowAdditionalStop?: boolean
  isShowCars?: boolean
  isShowCircle?: boolean
  isShowDestination?: boolean
  isShowOrigin?: boolean
  isShowPolyline?: boolean
}

export const mapViewStrategyStages = {
  liveTrip: 'LIVE_TRIP',
  showInit: 'SHOW_INIT',
  showNearbyDrivers: 'SHOW_NEARBY_DRIVERS',
  showPolyline: 'SHOW_POLYLINE'
}

const strategy: IStrategy[] = [
  {
    renderConditions: {},
    toFind: (stage: string) => stage === mapViewStrategyStages.showInit
  },
  {
    renderConditions: {
      isShowAdditionalStop: true,
      isShowDestination: true,
      isShowOrigin: true,
      isShowPolyline: true
    },
    toFind: (stage: string) => stage === mapViewStrategyStages.showPolyline
  },
  {
    renderConditions: {
      isShowAdditionalStop: true,
      isShowCars: true,
      isShowDestination: true,
      isShowOrigin: true,
      isShowPolyline: true
    },
    toFind: (stage: string) => stage === mapViewStrategyStages.liveTrip
  },
  {
    renderConditions: {
      isShowAdditionalStop: true,
      isShowCars: true,
      isShowDestination: true,
      isShowOrigin: true,
      isShowPolyline: true
    },
    toFind: (stage: string) => stage === mapViewStrategyStages.showNearbyDrivers
  }
]

interface IMapViewProps {
  bounds: any
  center?: ICoordinates
  circles?: CircleProps[]
  currentTrip?: IActiveTripResponseV2
  destination: ICoordinates | null
  isDraggable: boolean
  isModal?: boolean
  location: IRiderLocationObject | null | IServiceLocationObject
  locationType?: string | undefined
  markers?: IMarker[]
  notification?: any
  origin: ICoordinates | null
  polygon?: ICoordinates[] | null
  polyline: string | null
  renderStrategyStage: string
  waypoints: IWaypoints[] | null
  zone?: IZoneObject | null
  zoneId?: string
  zones: { [key: string]: IZoneObject } | {}
  zoom: number
  getDirections?: (dto: IGetGoogleMapPolyline) => void
  getOSMDirection?: (dto: IOSMDirectionReq) => void
  selectDropoff?: (locationInfo: ICoordinatesWithShortKeyDTO) => void
  selectLocation?: (locationInfo: ICoordinatesWithShortKeyDTO) => void
  selectPickup?: (locationInfo: ICoordinatesWithShortKeyDTO) => void
  setBounds?: (bounds: any) => void
  setPolyline?: (polyline: string) => void
}

interface IMapViewState {
  allowBoundFitting: boolean
  decodedPolyline: any | null
}

class MapView extends React.Component<IMapViewProps, IMapViewState> {
  public map: GoogleMap

  constructor(props: any) {
    super(props)
    this.state = {
      allowBoundFitting: true,
      decodedPolyline: null
    }
  }

  public componentDidUpdate(prevProps: IMapViewProps) {
    const {
      bounds: prevBounds,
      destination: prevDestination,
      markers: prevMarkers,
      origin: prevOrigin,
      polyline: prevPolyline,
      waypoints: prevWaypoints
    } = prevProps

    const {
      bounds,
      currentTrip,
      destination,
      isDraggable,
      markers,
      origin,
      polyline,
      waypoints,
      getDirections,
      getOSMDirection,
      setBounds,
      setPolyline
    } = this.props

    const { allowBoundFitting, decodedPolyline } = this.state

    const currTripBounds = currentTrip && currentTrip.bounds
    const currTripRoutePolyline = currentTrip && currentTrip.routePolyline

    if (!isDraggable) {
      if (currentTrip && currTripRoutePolyline && setPolyline && currTripRoutePolyline !== prevPolyline) {
        setPolyline(currTripRoutePolyline)
      }

      if (currentTrip && currTripBounds && setBounds && JSON.stringify(currTripBounds) !== JSON.stringify(prevBounds)) {
        setBounds(currTripBounds)
      }
    }

    if (isDraggable || (!isDraggable && currentTrip && !currTripRoutePolyline)) {
      if (
        this.map &&
        (JSON.stringify(origin) !== JSON.stringify(prevOrigin) ||
          JSON.stringify(destination) !== JSON.stringify(prevDestination) ||
          JSON.stringify(waypoints) !== JSON.stringify(prevWaypoints))
      ) {
        if (origin && destination && getDirections) {
          waypoints ? getDirections({ origin, waypoints, destination }) : getDirections({ origin, destination })
          if (getOSMDirection) {
            getOSMDirection({
              location: [[origin.lng, origin.lat], [destination.lng, destination.lat]]
            })
          }
        }
      }
    }

    if (polyline && prevPolyline !== polyline) {
      const decodedPolyline = google.maps.geometry.encoding.decodePath(polyline.toString())
      this.setState({ decodedPolyline })
    }

    if (prevPolyline && !polyline) {
      this.setState({ decodedPolyline: null })
    }

    if ((!markers || !markers.length) && bounds && JSON.stringify(prevBounds) !== JSON.stringify(bounds)) {
      allowBoundFitting ? this.map.fitBounds(bounds) : this.setState({ allowBoundFitting: true })
    }

    if (markers && JSON.stringify(prevMarkers) !== JSON.stringify(markers)) {
      this.setState({ allowBoundFitting: true })
    }

    if ((origin || destination) && (markers && markers.length)) {
      if ((origin && origin.lat) || (destination && destination.lat)) {
        const pickupLocation = markers && markers.filter((marker): marker is IMarker => marker.type === 'origin')
        const dropOffLocation = markers && markers.filter((marker): marker is IMarker => marker.type === 'destination')
        const addionalStopLocation =
          markers && markers.filter((marker): marker is IMarker => marker.type === 'additionalStop')
        const locationsArray = [...pickupLocation, ...addionalStopLocation, ...dropOffLocation]
        this.fitMarkerBounds(locationsArray, decodedPolyline, true, false)
      }
    }
  }

  public render() {
    const {
      center,
      circles,
      currentTrip,
      destination,
      isModal,
      location,
      locationType,
      markers,
      origin,
      polygon,
      renderStrategyStage,
      zone,
      zoneId,
      zones,
      zoom
    } = this.props

    const { allowBoundFitting, decodedPolyline } = this.state

    const renderStrategy = strategy.find(it => it.toFind(renderStrategyStage))

    return (
      <span className='w-100 relative'>
        <GoogleMapComponent
          center={origin || center}
          circles={circles}
          containerElement={<div className='MapContainer' />}
          currentTrip={currentTrip}
          decodedPolyline={decodedPolyline}
          destination={destination}
          isDraggable={true}
          isModal={isModal}
          location={location}
          locationType={locationType}
          mapElement={<div className='GoogleMap' />}
          markers={markers}
          origin={origin}
          polygon={polygon}
          renderStrategy={renderStrategy ? renderStrategy.renderConditions : {}}
          zone={zone}
          zoneId={zoneId}
          zones={zones}
          zoom={zoom}
          onDestPinChanged={this.onDestPinChanged}
          onMapMounted={this.handleMapMounted}
          onOriginPinChanged={this.onOriginPinChanged}
          selectLocation={this.onSelectLocation}
        />
        {markers && allowBoundFitting && (
          <button className='absolute left-1 bottom-1 mb3 btn' onClick={this.handleMapRefresh}>
            Refresh map
          </button>
        )}
      </span>
    )
  }

  public handleMapRefresh = () => {
    const { markers } = this.props
    const { decodedPolyline } = this.state
    if (markers) {
      this.fitMarkerBounds(markers, decodedPolyline)
    }
  }

  public handleMapMounted = (map: any) => {
    this.map = map
  }

  private onSelectLocation = (event: google.maps.MouseEvent) => {
    const { selectLocation } = this.props
    if (selectLocation) {
      const { lat, lng } = event.latLng
      const coordinates = {
        lat: lat(),
        lng: lng()
      }
      selectLocation(coordinates)
    }
  }

  private onOriginPinChanged = (event: google.maps.MouseEvent) => {
    const { selectPickup } = this.props
    if (selectPickup) {
      const { lat, lng } = event.latLng
      const coordinates = {
        lat: lat(),
        lng: lng()
      }
      this.setState({ allowBoundFitting: false })
      selectPickup(coordinates)
    }
  }

  private onDestPinChanged = (event: google.maps.MouseEvent) => {
    const { selectDropoff } = this.props
    if (selectDropoff) {
      const { lat, lng } = event.latLng
      const coordinates = {
        lat: lat(),
        lng: lng()
      }
      this.setState({ allowBoundFitting: false })
      selectDropoff(coordinates)
    }
  }

  private fitMarkerBounds = (
    markers?: ICoordinates[],
    polyline?: any,
    ignoreCarMarker?: boolean,
    ignoreZoomLogic?: boolean
  ) => {
    if (!markers || !markers.length) {
      return
    }
    const bounds = new window.google.maps.LatLngBounds()
    let makerArray = markers
    if (ignoreCarMarker === true) {
      makerArray = markers.filter(item => item.type !== 'car')
    }
    makerArray.forEach(m => {
      bounds.extend(m)
    })

    if (polyline) {
      polyline.forEach((p: any) => bounds.extend(p))
    }

    // Don't zoom in too far on only one marker
    if (bounds.getNorthEast().equals(bounds.getSouthWest()) && ignoreZoomLogic !== true) {
      const extendPoint1 = new google.maps.LatLng(
        bounds.getNorthEast().lat() + 0.01,
        bounds.getNorthEast().lng() + 0.01
      )
      const extendPoint2 = new google.maps.LatLng(
        bounds.getNorthEast().lat() - 0.01,
        bounds.getNorthEast().lng() - 0.01
      )
      bounds.extend(extendPoint1)
      bounds.extend(extendPoint2)
    }
    this.map.fitBounds(bounds)
  }
}

export default MapView



// WEBPACK FOOTER //
// ./src/components/MapView.tsx